import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseUser } from "./use-supabase-user";

// Centralized roles fetching with React Query to dedupe across components
async function fetchUserRoles(userId: string) {
  // Admin check (RPC)
  const { data: isAdminResult, error: isAdminError } = await supabase.rpc(
    "is_admin",
    { user_id: userId }
  );
  if (isAdminError) throw isAdminError;

  // Super admin check (RPC)
  const { data: isSuperAdminResult, error: isSuperAdminError } = await supabase.rpc(
    "is_super_admin",
    { user_id: userId }
  );
  if (isSuperAdminError) throw isSuperAdminError;

  // Unassigned admin check (RPC)
  const { data: isUnassignedAdminResult, error: isUnassignedAdminError } = await supabase.rpc(
    "is_unassigned_admin",
    { user_id: userId }
  );
  if (isUnassignedAdminError) throw isUnassignedAdminError;

  // Manager check (RPC)
  const { data: isManagerResult, error: isManagerError } = await supabase.rpc(
    "is_manager",
    { user_id: userId }
  );
  if (isManagerError) throw isManagerError;

  // Get admin team assignments
  const { data: adminTeams, error: adminTeamsError } = await supabase
    .from("admin_teams")
    .select("team_id")
    .eq("admin_id", userId);
  if (adminTeamsError) throw adminTeamsError;

  // Roles list
  const { data: rolesData, error: rolesError } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  if (rolesError) throw rolesError;

  const roles = (rolesData || []).map((r: any) =>
    typeof r.role === "string" ? r.role : String(r.role)
  );

  // Check if user is a contributor
  const { data: contributorData, error: contributorError } = await supabase
    .from("admin_contributors")
    .select("id")
    .eq("contributor_admin_id", userId)
    .maybeSingle();
  if (contributorError) throw contributorError;

  return {
    roles,
    isAdmin: isAdminResult === true,
    isSuperAdmin: isSuperAdminResult === true,
    isUnassignedAdmin: isUnassignedAdminResult === true,
    isManager: isManagerResult === true,
    adminTeamIds: (adminTeams || []).map(t => t.team_id),
    isContributor: !!contributorData,
  } as const;
}

export const useUserRoles = () => {
  const { user } = useSupabaseUser();

  // Always call useQuery to keep hooks order stable
  const query = useQuery({
    queryKey: ["userRoles", user?.id],
    queryFn: () => fetchUserRoles(user!.id),
    enabled: !!user?.id,
    staleTime: 120_000, // 2 minutes
    gcTime: 600_000, // 10 minutes cache
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const defaults = { 
    roles: [] as string[], 
    isAdmin: false, 
    isSuperAdmin: false,
    isUnassignedAdmin: false,
    isManager: false,
    adminTeamIds: [] as string[],
    isContributor: false
  };
  const data = (user ? query.data : defaults) || defaults;

  return {
    roles: data.roles,
    isAdmin: data.isAdmin,
    isSuperAdmin: data.isSuperAdmin,
    isUnassignedAdmin: data.isUnassignedAdmin,
    isManager: data.isManager,
    adminTeamIds: data.adminTeamIds,
    isContributor: data.isContributor,
    isLoading: user ? query.isLoading : false,
    error: user ? (query.error as any)?.message ?? null : null,
    hasRole: (role: string) => data.roles.some((r) => r.toLowerCase() === role.toLowerCase()),
    canAccessTeam: (teamId: string) => {
      // Super admins can access all teams
      if (data.isSuperAdmin) return true;
      // Unassigned admins cannot access any teams
      if (data.isUnassignedAdmin) return false;
      // Team admins can only access assigned teams
      return data.adminTeamIds.includes(teamId);
    },
  };
};
